/**
 * JSONBeautifier Service
 * 
 * Provides JSON formatting, beautification, and minification functionality.
 * Handles proper indentation, whitespace management, and preserves data integrity.
 */

export class JSONBeautifier {
    /**
     * Beautifies JSON string with proper formatting and indentation
     * @param {string} jsonString - The JSON string to beautify
     * @param {Object} options - Formatting options
     * @param {number} options.indent - Number of spaces for indentation (default: 2)
     * @param {boolean} options.sortKeys - Whether to sort object keys (default: false)
     * @param {boolean} options.insertFinalNewline - Whether to add final newline (default: false)
     * @returns {Object} Result object with success status and formatted JSON or error
     */
    static beautify(jsonString, options = {}) {
        const {
            indent = 2,
            sortKeys = false,
            insertFinalNewline = false
        } = options

        try {
            // Validate input
            if (typeof jsonString !== 'string') {
                throw new Error('Input must be a string')
            }

            if (!jsonString.trim()) {
                return {
                    success: false,
                    error: 'Empty input provided',
                    original: jsonString
                }
            }

            // Parse JSON to validate syntax and structure
            const parsed = JSON.parse(jsonString)
            
            // Create replacer function for key sorting if requested
            const replacer = sortKeys ? this._createSortingReplacer() : null
            
            // Format with specified indentation
            let formatted = JSON.stringify(parsed, replacer, indent)
            
            // Add final newline if requested
            if (insertFinalNewline && !formatted.endsWith('\n')) {
                formatted += '\n'
            }

            return {
                success: true,
                formatted,
                original: jsonString,
                stats: {
                    originalLength: jsonString.length,
                    formattedLength: formatted.length,
                    compressionRatio: (formatted.length / jsonString.length).toFixed(2)
                }
            }

        } catch (error) {
            return {
                success: false,
                error: error.message,
                original: jsonString,
                details: this._parseJSONError(error, jsonString)
            }
        }
    }

    /**
     * Minifies JSON string by removing unnecessary whitespace
     * @param {string} jsonString - The JSON string to minify
     * @returns {Object} Result object with success status and minified JSON or error
     */
    static minify(jsonString) {
        try {
            // Validate input
            if (typeof jsonString !== 'string') {
                throw new Error('Input must be a string')
            }

            if (!jsonString.trim()) {
                return {
                    success: false,
                    error: 'Empty input provided',
                    original: jsonString
                }
            }

            // Parse and stringify without formatting
            const parsed = JSON.parse(jsonString)
            const minified = JSON.stringify(parsed)

            return {
                success: true,
                minified,
                original: jsonString,
                stats: {
                    originalLength: jsonString.length,
                    minifiedLength: minified.length,
                    compressionRatio: (minified.length / jsonString.length).toFixed(2),
                    spaceSaved: jsonString.length - minified.length
                }
            }

        } catch (error) {
            return {
                success: false,
                error: error.message,
                original: jsonString,
                details: this._parseJSONError(error, jsonString)
            }
        }
    }

    /**
     * Removes unnecessary whitespace while maintaining basic readability
     * @param {string} jsonString - The JSON string to clean
     * @returns {Object} Result object with success status and cleaned JSON or error
     */
    static removeEmptySpace(jsonString) {
        try {
            // Validate input
            if (typeof jsonString !== 'string') {
                throw new Error('Input must be a string')
            }

            if (!jsonString.trim()) {
                return {
                    success: false,
                    error: 'Empty input provided',
                    original: jsonString
                }
            }

            // Parse to validate, then format with minimal spacing
            const parsed = JSON.parse(jsonString)
            
            // Use single space indentation for compact but readable format
            const cleaned = JSON.stringify(parsed, null, 1)

            return {
                success: true,
                cleaned,
                original: jsonString,
                stats: {
                    originalLength: jsonString.length,
                    cleanedLength: cleaned.length,
                    compressionRatio: (cleaned.length / jsonString.length).toFixed(2),
                    spaceSaved: jsonString.length - cleaned.length
                }
            }

        } catch (error) {
            return {
                success: false,
                error: error.message,
                original: jsonString,
                details: this._parseJSONError(error, jsonString)
            }
        }
    }

    /**
     * Validates if a string is valid JSON without formatting
     * @param {string} jsonString - The JSON string to validate
     * @returns {boolean} True if valid JSON, false otherwise
     */
    static isValidJSON(jsonString) {
        try {
            JSON.parse(jsonString)
            return true
        } catch {
            return false
        }
    }

    /**
     * Gets formatting statistics for a JSON string
     * @param {string} jsonString - The JSON string to analyze
     * @returns {Object} Statistics about the JSON structure
     */
    static getStats(jsonString) {
        try {
            const parsed = JSON.parse(jsonString)
            
            return {
                isValid: true,
                length: jsonString.length,
                structure: this._analyzeStructure(parsed),
                whitespace: this._analyzeWhitespace(jsonString)
            }
        } catch (error) {
            return {
                isValid: false,
                length: jsonString.length,
                error: error.message,
                whitespace: this._analyzeWhitespace(jsonString)
            }
        }
    }

    /**
     * Creates a replacer function for sorting object keys
     * @private
     * @returns {Function} Replacer function for JSON.stringify
     */
    static _createSortingReplacer() {
        return function(key, value) {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                // Sort object keys
                const sortedObj = {}
                Object.keys(value).sort().forEach(sortedKey => {
                    sortedObj[sortedKey] = value[sortedKey]
                })
                return sortedObj
            }
            return value
        }
    }

    /**
     * Parses JSON error to provide better error information
     * @private
     * @param {Error} error - The JSON parsing error
     * @param {string} jsonString - The original JSON string
     * @returns {Object} Detailed error information
     */
    static _parseJSONError(error, jsonString) {
        const message = error.message
        
        // Try to extract position information from error message
        const positionMatch = message.match(/position (\d+)/i)
        const lineMatch = message.match(/line (\d+)/i)
        const columnMatch = message.match(/column (\d+)/i)
        
        let position = null
        let line = null
        let column = null
        
        if (positionMatch) {
            position = parseInt(positionMatch[1], 10)
            // Calculate line and column from position
            const lines = jsonString.substring(0, position).split('\n')
            line = lines.length
            column = lines[lines.length - 1].length + 1
        } else if (lineMatch && columnMatch) {
            line = parseInt(lineMatch[1], 10)
            column = parseInt(columnMatch[1], 10)
        }
        
        return {
            message,
            position,
            line,
            column,
            context: position ? this._getErrorContext(jsonString, position) : null
        }
    }

    /**
     * Gets context around an error position
     * @private
     * @param {string} jsonString - The JSON string
     * @param {number} position - Error position
     * @returns {Object} Context information
     */
    static _getErrorContext(jsonString, position) {
        const contextSize = 20
        const start = Math.max(0, position - contextSize)
        const end = Math.min(jsonString.length, position + contextSize)
        
        return {
            before: jsonString.substring(start, position),
            at: jsonString.charAt(position),
            after: jsonString.substring(position + 1, end),
            fullContext: jsonString.substring(start, end)
        }
    }

    /**
     * Analyzes the structure of parsed JSON
     * @private
     * @param {*} data - Parsed JSON data
     * @returns {Object} Structure analysis
     */
    static _analyzeStructure(data) {
        const analysis = {
            type: Array.isArray(data) ? 'array' : typeof data,
            depth: 0,
            keys: 0,
            values: 0,
            arrays: 0,
            objects: 0,
            primitives: 0
        }

        const analyze = (obj, currentDepth = 0) => {
            analysis.depth = Math.max(analysis.depth, currentDepth)
            
            if (Array.isArray(obj)) {
                analysis.arrays++
                obj.forEach(item => analyze(item, currentDepth + 1))
            } else if (obj && typeof obj === 'object') {
                analysis.objects++
                Object.keys(obj).forEach(key => {
                    analysis.keys++
                    analysis.values++
                    analyze(obj[key], currentDepth + 1)
                })
            } else {
                analysis.primitives++
            }
        }

        analyze(data)
        return analysis
    }

    /**
     * Analyzes whitespace in JSON string
     * @private
     * @param {string} jsonString - The JSON string
     * @returns {Object} Whitespace analysis
     */
    static _analyzeWhitespace(jsonString) {
        const spaces = (jsonString.match(/ /g) || []).length
        const tabs = (jsonString.match(/\t/g) || []).length
        const newlines = (jsonString.match(/\n/g) || []).length
        const carriageReturns = (jsonString.match(/\r/g) || []).length
        
        const totalWhitespace = spaces + tabs + newlines + carriageReturns
        const totalChars = jsonString.length
        
        return {
            spaces,
            tabs,
            newlines,
            carriageReturns,
            total: totalWhitespace,
            percentage: totalChars > 0 ? ((totalWhitespace / totalChars) * 100).toFixed(2) : 0
        }
    }
}